/**
 * The session flow's talk-to-the-server layer, and the one place that decides
 * WHERE a rating is written.
 *
 * There are two devices in this product and they are not the same client:
 *
 *  - The **owner's device** on a shared-device night is offline-first. Ratings
 *    go through `mutate()` into the IndexedDB outbox, atomically, and replicate
 *    through the ordinary sync protocol. That is what makes a rating survive a
 *    dead router in a friend's basement.
 *  - A **guest device** on a QR night has no IndexedDB store, no owner session
 *    and no sync cursor. It holds one join-grant bearer token, scoped by the
 *    server to exactly one seat in one session, and it writes over HTTP. Running
 *    the outbox there would replicate somebody else's whole library onto a
 *    phone that was borrowed for one evening.
 *
 * `commitRating` is the seam. Screens describe the rating; this decides the
 * transport from the capture mode and the role, and nothing above it has to
 * care.
 *
 * The token lives in `sessionStorage` on purpose: a borrowed phone must not
 * still be holding a grant next Tuesday.
 */

import { getOutboxStorage, mutate, outboxPaused, pending, readLocal } from "../../store/outbox.ts";
import type { OutboxOp } from "../../store/outbox.ts";
import { idbAvailable } from "../../store/idb.ts";
import { adoptServerRow, forgetSession, reportUnsentWrites, syncNow } from "../../store/sync.ts";
import { ulid } from "../../../shared/ids.ts";
import type {
  CaptureMode,
  Entity,
  PlaySession,
  SessionParticipant,
} from "../../../shared/types.ts";
// Type-only: erased at build time, so no server module is ever pulled into the
// client bundle. The read model is the contract, so it is imported, not retyped.
import type {
  ClaimResult,
  CompleteResult,
  DeleteSessionResult,
  JoinOffer,
  ParticipantView,
  SessionGraph,
  SessionState,
  StableAnswer,
  StartSessionInput,
  SubmitRatingResult,
} from "../../../server/sessions.ts";

export type {
  CompleteResult,
  DeleteSessionResult,
  JoinOffer,
  ParticipantView,
  SessionGraph,
  SessionState,
  StableAnswer,
  StartSessionInput,
};

// ---------------------------------------------------------------------------
// Grants
// ---------------------------------------------------------------------------

export interface Grant {
  token: string;
  session_id: string;
  participant_id: string;
}

const GRANT_KEY = "pb-grant";

/** sessionStorage can throw outright in a locked-down browser; never fatal. */
function store(): Storage | null {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

export function saveGrant(grant: Grant): void {
  try {
    store()?.setItem(GRANT_KEY, JSON.stringify(grant));
  } catch {
    // A guest can still rate this evening; only the reload survives less well.
  }
}

/** The grant for this session, if this device is a guest in it. */
export function readGrant(sessionId?: string): Grant | null {
  try {
    const raw = store()?.getItem(GRANT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<Grant>;
    if (!parsed.token || !parsed.session_id || !parsed.participant_id) return null;
    if (sessionId && parsed.session_id !== sessionId) return null;
    return parsed as Grant;
  } catch {
    return null;
  }
}

export function clearGrant(): void {
  try {
    store()?.removeItem(GRANT_KEY);
  } catch {
    // Nothing to do — the token expires with the session anyway.
  }
}

// ---------------------------------------------------------------------------
// Transport
// ---------------------------------------------------------------------------

export class ApiError extends Error {
  readonly status: number;
  /**
   * Whether `message` is a sentence the server wrote for a person.
   *
   * This app repeats the server's own words on purpose — "this game night is
   * already finished" is not "check the wifi", and only the server knows which
   * it is. HTTP boilerplate is not that: `Internal Server Error` read out at a
   * game table names nothing anybody there can act on, and it reached the screen
   * because the same field carries both. A 5xx is boilerplate even when it
   * arrives with a body; a server that fell over is not explaining itself.
   */
  readonly serverSaid: boolean;
  constructor(message: string, status: number, serverSaid = false) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.serverSaid = serverSaid;
  }
}

/**
 * Statuses that are about *when*, not about the request. The retryable 4xx.
 *
 *  - `401` is the one 4xx a *credential* can fix rather than a different
 *    request: an expired cookie or a grant that outlived its session. The app
 *    already has a path that replaces one (`subscribeAuthLost` puts the sign-in
 *    page back up), so the same bytes really can succeed on a later attempt.
 *  - `408` and `429` say so in as many words — a timeout and a rate limit are
 *    both invitations to come back, and the backoff ladder is exactly that.
 */
const RETRYABLE_4XX: ReadonlySet<number> = new Set([401, 408, 429]);

/**
 * Whether the server has refused this write for good.
 *
 * The line the client was missing. A 5xx, a timeout and a dead network are all
 * "not right now" — the same request sent again is the correct response, and
 * the backoff ladder in `store/sync.ts` exists for them. A permanent 4xx is
 * "not this request, ever": `409 that answer is already revealed` is a decision
 * the server has already made about a row it has already published, and the
 * fifth identical attempt is refused in the same words as the first. Putting one
 * on the ladder buys a phone that retries forever under a banner promising the
 * table a rating is on its way, and a navigation guard asking whether to risk
 * losing something that was never in flight.
 *
 * Taken as a whole class rather than a list of the three codes QA happened to
 * hit: every remaining 4xx this app can produce — `400` a malformed body, `403`
 * a seat on another phone, `404` a row that is not there, `409` a settled
 * answer or a finished night, `410` an expired join code, `422` — describes the
 * *request*, and this client sends the identical request on every retry.
 *
 * Anything that never got as far as an HTTP status (a `TypeError` from a dead
 * radio, an aborted fetch) is not a refusal at all and stays on the ladder.
 */
export function permanentRefusal(err: unknown): boolean {
  if (!(err instanceof ApiError)) return false;
  return err.status >= 400 && err.status < 500 && !RETRYABLE_4XX.has(err.status);
}

/**
 * Whether a refusal means the grant this device presented is dead for good — and
 * therefore that the token in `sessionStorage` is rubbish and must be thrown out.
 *
 * **Why `presented` is in the signature rather than in a comment.** A bare "410
 * means the grant is dead" is wrong, and wrong in the direction that costs a
 * guest their evening. This app answers `410` at three places and only one of
 * them is about a token:
 *
 *  - `resolveActor` in `src/server/index.ts`, when `authorizeGrant` returns
 *    nothing. The one we want.
 *  - `readJoinRoster`, "join code expired". About the *code*, not the phone: a
 *    guest whose code aged out mid-evening is still holding a perfectly live
 *    grant, and their seat, and their answers.
 *  - `claimParticipant`, the same sentence on the POST behind the seat picker.
 *
 * The second and third are reachable only through `fetchJoinRoster` and
 * `claimSeat`, and neither sends a bearer token; every route that *does* take one
 * resolves it in `resolveActor` before the handler runs, and no handler a live
 * grant can reach throws `410` at all. So "did this request carry the grant"
 * separates the three cleanly, with no prose parsed and nothing to keep in step
 * with a sentence on the other side of the wire. Asking the caller to hand over
 * the token it sent makes that the mechanical precondition it is, instead of a
 * rule the next reader has to find in a comment and remember.
 *
 * **Why this refusal is permanent when the neighbouring 410s are not.** The
 * server deliberately will not say *which* dead it is — `DEAD_GRANT_MESSAGE` is
 * one sentence for revoked, expired and invented alike, because a reply that told
 * them apart would tell a prober which random strings were once real tokens. It
 * does not need to, because all three are inert in the same way and nothing in
 * the app resurrects any of them. A revoked grant carries `revoked = 1`, which
 * only `takeBackSeat` and `reclaimAnswerlessSeat` ever set and nothing clears —
 * completion's expiry bump is explicitly filtered to `revoked = 0`. An expired
 * one is past `expires_at`, and that same bump is filtered to `expires_at > now`,
 * so a token already dead of old age is never revived by the end of the night. An
 * invented one never existed. A finished night is the case to keep straight and
 * it is not in this set at all: `completeSession` leaves live grants alive with
 * their window pushed out to a day precisely so the guests can read the board
 * they spent the evening filling in (`9c13852`), so that phone is answered `200`
 * here and nothing of its is thrown away.
 *
 * Which leaves the one thing this cannot be asked about: a request that never got
 * an answer. A dead radio, a captive portal and a 5xx are all "not now" and none
 * of them is evidence about a token — `ApiError` is not even thrown for the first
 * two. Destroying a guest's grant because the wifi blinked would hand them the
 * same lockout by a different route, so anything that is not this exact refusal
 * leaves the token where it is.
 */
export function deadGrantRefusal(err: unknown, presented: string | null | undefined): boolean {
  if (!presented) return false;
  return err instanceof ApiError && err.status === 410;
}

export function authHeaders(token: string | null | undefined): Record<string, string> {
  return token ? { authorization: `Bearer ${token}` } : {};
}

/** `?as=` names whose turn it is on a shared device; the server redacts to it. */
export function sessionStateUrl(
  sessionId: string,
  opts: { as?: string | null } = {},
): string {
  const q = opts.as ? `?as=${encodeURIComponent(opts.as)}` : "";
  return `/api/sessions/${encodeURIComponent(sessionId)}${q}`;
}

export function socketUrl(opts: { sessionId: string; token?: string | null }): string {
  const proto = window.location.protocol === "https:" ? "wss:" : "ws:";
  const base = `${proto}//${window.location.host}/api/ws`;
  // A browser cannot set headers on a WebSocket handshake, so the grant token
  // rides the query string; the host authenticates with its owner cookie.
  return opts.token
    ? `${base}?token=${encodeURIComponent(opts.token)}`
    : `${base}?session=${encodeURIComponent(opts.sessionId)}`;
}

async function request<T>(
  url: string,
  init: RequestInit & { token?: string | null } = {},
): Promise<T> {
  const { token, headers, ...rest } = init;
  const res = await fetch(url, {
    ...rest,
    credentials: "same-origin",
    headers: {
      ...(rest.body ? { "content-type": "application/json" } : {}),
      ...authHeaders(token),
      ...(headers as Record<string, string> | undefined),
    },
  });
  if (!res.ok) {
    // The server sends `{ error }`; a proxy or an offline SW might not.
    let message = `${res.status}`;
    let serverSaid = false;
    try {
      const body = (await res.json()) as { error?: string };
      if (body.error) {
        message = body.error;
        serverSaid = res.status < 500;
      }
    } catch {
      message = res.statusText || message;
    }
    throw new ApiError(message, res.status, serverSaid);
  }
  return (await res.json()) as T;
}

// ---------------------------------------------------------------------------
// Session endpoints
// ---------------------------------------------------------------------------

export function fetchSessionState(
  sessionId: string,
  opts: { as?: string | null; token?: string | null } = {},
): Promise<SessionState> {
  return request<SessionState>(sessionStateUrl(sessionId, opts), { token: opts.token });
}

/** What a stored grant turns out to be worth, when somebody bothers to ask. */
export type GrantVerdict = "alive" | "dead" | "unknown";

/**
 * Ask the server whether the grant on this phone is still a grant.
 *
 * **This exists because nothing on the client ever asked.** Live QA, multi-device:
 * a guest scanned in and took a seat, the host took that seat back — a real
 * control, for the phone that got handed over or left the table — and the server
 * revoked the grant, as it must. The guest's browser then presented the same dead
 * token on every poll, was refused every time, and *kept it*. Re-scanning the
 * code could not help, because the join screen preferred the stored grant over
 * the fresh join it had just been handed: `readGrant` returned something, so the
 * screen said "you are in", and "back to rating" led straight back to the
 * refusal. Permanently locked out from that browser, all evening; a private window
 * worked, which is what says the dead state is here rather than on the server.
 *
 * The request sent is exactly the one the rating screen makes — the redacted read
 * of the guest's own table, with the guest's own token — rather than a cheaper
 * probe invented for this. Nothing else answers the question that actually
 * matters, which is not "does this string parse" but "may this phone still read
 * the night it thinks it is in", and a purpose-built endpoint would be a second
 * definition of a live grant to keep in step with the first. It is also why the
 * answer can be trusted at all now: until `98e19d3` the browser's own HTTP cache
 * replayed an old `410` at a request whose credentials had since become fine, so
 * a check like this would have been asking the cache rather than the server.
 *
 * Deliberately does not clear anything. Deciding a token is dead and throwing it
 * away are separate acts with different consequences — the token is what keeps
 * `/session/:id` a public path for a phone with no account (`isPublicPath`), so
 * dropping it costs the holder the honest sentence on their screen and hands them
 * the front door instead. Only the join screen throws one away, because that is
 * the one screen that can offer a seat back in the same breath.
 *
 * `read` is injected so the three answers can be stepped without a network.
 */
export async function grantVerdict(
  grant: Grant,
  read: (
    sessionId: string,
    opts: { token?: string | null },
  ) => Promise<unknown> = fetchSessionState,
): Promise<GrantVerdict> {
  try {
    await read(grant.session_id, { token: grant.token });
    return "alive";
  } catch (err) {
    return deadGrantRefusal(err, grant.token) ? "dead" : "unknown";
  }
}

export function startSession(input: StartSessionInput): Promise<SessionGraph> {
  return request<SessionGraph>("/api/sessions", {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export function openJoin(sessionId: string): Promise<JoinOffer> {
  return request<JoinOffer>(`/api/sessions/${encodeURIComponent(sessionId)}/join`, {
    method: "POST",
  });
}

/**
 * The once-per-game aspects this game has already been answered on.
 *
 * The one thing on the setup screen that cannot be computed from the device's
 * own rows: the local store holds the nights and their frozen aspects, but the
 * ratings that decide whether a question was actually answered are the server's
 * to count — on a QR night every other phone wrote its answers straight over
 * HTTP. Never on the critical path: setup already refuses to start a night
 * offline, and a lookup that does not come back only means the group is asked a
 * question they could have skipped.
 */
export function fetchStableAnswers(gameId: string): Promise<StableAnswer[]> {
  return request<StableAnswer[]>(`/api/games/${encodeURIComponent(gameId)}/stable-answers`);
}

/** What taking a seat back cost, in seats and in thrown-away answers. */
export interface TakeBackResult {
  released: number;
  discarded: number;
}

/**
 * The host taking back a seat a guest phone answered on.
 *
 * Never sent from a borrowed phone: it carries the owner's cookie and no token,
 * and the server refuses the route outright for any bearer credential. The
 * answers on the seat are discarded by this call, so the screen has to have said
 * so and been agreed with before it is made.
 */
export function takeBackSeat(sessionId: string, participantId: string): Promise<TakeBackResult> {
  return request<TakeBackResult>(
    `/api/sessions/${encodeURIComponent(sessionId)}/participants/${encodeURIComponent(
      participantId,
    )}/take-back`,
    { method: "POST" },
  );
}

export function completeSession(sessionId: string): Promise<CompleteResult> {
  return request<CompleteResult>(`/api/sessions/${encodeURIComponent(sessionId)}/complete`, {
    method: "POST",
  });
}

/**
 * What `finishSession` did. `unsent` is a screen, not an error.
 *
 * `reason` tells "still on its way" from "not going anywhere": a paused outbox
 * (signed out, session expired, app upgraded) will not move on its own, and
 * telling the host to wait for it is telling them to wait forever.
 */
export type FinishOutcome =
  | { kind: "unsent"; count: number; reason: "queued" | "paused" }
  | { kind: "server"; result: CompleteResult };

interface FinishDeps {
  drain: () => Promise<void>;
  queued: () => Promise<OutboxOp[]>;
  available: () => boolean;
  paused: () => boolean;
  complete: (sessionId: string) => Promise<CompleteResult>;
  reconcile: (session: PlaySession) => Promise<void>;
}

/**
 * Bring this device's copy of the night in line with the server that just ended
 * it.
 *
 * Completion is the server's decision — it owns the status and the reveal, and it
 * owns what becomes of the join code, which since `9c13852` is nothing at all:
 * the code stays on the row on purpose, so a guest re-scanning after the last tap
 * lands on "this night is already finished" rather than on the "that link did not
 * open a game" loop production served. So the local row is replaced with the one
 * the server answered with rather than patched to what this device assumes
 * happened, and a device that second-guessed the code would put that dead end
 * back on its own copy.
 *
 * The pull behind it is not a duplicate of the drain that ran before the
 * completion. On a QR night every other phone wrote its ratings straight over
 * HTTP and this device has never seen a single one of them, so without it the
 * shelf shows a finished night with nothing in it. It is not awaited: the row
 * above is what the screens read first, and a slow pull must not sit on top of
 * the one tap the whole evening builds to.
 */
async function reconcileCompletion(session: PlaySession): Promise<void> {
  await adoptServerRow("play_session", session);
  void syncNow().catch(() => undefined);
}

/**
 * The whole outbox is scanned, not a page of it: an op left behind past the
 * ordinary drain batch is exactly the one that must still be found here.
 */
const QUEUE_SCAN_LIMIT = 10_000;

/**
 * The queued ops that would be lost by completing THIS session.
 *
 * Only rating rows of this session count. The device outbox is one queue for the
 * whole app, so a renamed profile, an edited game or a rating from last week's
 * table all sit in it too — and none of them is a reason the host cannot end
 * tonight. Counting them was a dead end with nothing the host could do about it.
 *
 * `session_id` rides in every rating patch `commitRating` writes. A headline row
 * is keyed by the session, so its id answers the question too; an overall row is
 * keyed by the session plus a suffix, so its id answers it as a prefix match.
 */
const RATING_ENTITIES = new Set<string>([
  "aspect_rating",
  "subquestion_rating",
  "headline_rating",
  "overall_rating",
]);

export function sessionRatingOps(sessionId: string, ops: readonly OutboxOp[]): OutboxOp[] {
  const sessionPrefix = overallRowId(sessionId);
  const headlinePrefix = ratingRowId(sessionId, "");
  return ops.filter((op) => {
    // Sub-questions count too: completing the night makes the sync guard refuse
    // every rating of it, so one left in the queue dead-letters exactly like an
    // aspect rating would.
    if (!RATING_ENTITIES.has(op.entity)) return false;
    const owner = op.patch["session_id"];
    if (typeof owner === "string") return owner === sessionId;
    if (op.entity === "headline_rating") return op.id.startsWith(headlinePrefix);
    return op.entity === "overall_rating" && op.id.startsWith(sessionPrefix);
  });
}

/**
 * Finish the night — but not while this device is still holding ratings.
 *
 * Completing is one-way: the sync guard refuses a rating whose session is no
 * longer `DRAFT`, so anything left in the outbox at that moment dead-letters and
 * the person who typed it never gets it back. So the queue is pushed first, and
 * if anything of THIS session is still owed the completion does not happen.
 *
 * A device with no usable IndexedDB has no outbox to hold anything back, and
 * must never be blocked by a queue it cannot even read — that path completes
 * over HTTP exactly as it did before the outbox existed.
 *
 * A completion the server grants is then reconciled locally before this returns,
 * because every screen the group is about to look at reads the local row.
 *
 * The dependencies are injected so the ordering can be tested without a network.
 */
export async function finishSession(
  sessionId: string,
  deps: Partial<FinishDeps> = {},
): Promise<FinishOutcome> {
  const drain = deps.drain ?? syncNow;
  const queued = deps.queued ?? (() => pending(QUEUE_SCAN_LIMIT));
  const available = deps.available ?? idbAvailable;
  const paused = deps.paused ?? (() => outboxPaused().paused);
  const complete = deps.complete ?? completeSession;
  const reconcile = deps.reconcile ?? reconcileCompletion;
  await drain();
  if (available()) {
    const mine = sessionRatingOps(sessionId, await queued());
    if (mine.length > 0) {
      return { kind: "unsent", count: mine.length, reason: paused() ? "paused" : "queued" };
    }
  }
  const result = await complete(sessionId);

  // The 200 is not the end of it. The shelf, the catalog and the game page all
  // read the *local* row, and the local row still says DRAFT — so the group taps
  // the last button of the evening and lands on a screen insisting the night is
  // still being rated, next to a sync badge honestly reporting that everything
  // is synced, because a stale copy is not something the outbox knows it owes
  // anyone. Only a reload fixed it, and nobody reloads an app they have just
  // finished a game night in.
  //
  // Only where there is a local library to reconcile. A guest phone holds a
  // grant and no store of its own, and its cookie-less pull would come back 401,
  // pause an outbox it does not have, and put the sign-in page over the reveal.
  if (result.ok && available() && readGrant(sessionId) === null) {
    // The server has already ended the night. A local write that fails is a
    // stale screen the next pull repairs, not a reason to report a failure over
    // a completion that happened.
    await reconcile(result.session).catch(() => undefined);
  }
  return { kind: "server", result };
}

// ---------------------------------------------------------------------------
// Throwing a night away
// ---------------------------------------------------------------------------

/** The wire call. `cancelSession` below is what screens should reach for. */
export function deleteSession(sessionId: string): Promise<DeleteSessionResult> {
  return request<DeleteSessionResult>(`/api/sessions/${encodeURIComponent(sessionId)}`, {
    method: "DELETE",
  });
}

interface CancelDeps {
  remove: (sessionId: string) => Promise<DeleteSessionResult>;
  queued: () => Promise<OutboxOp[]>;
  drop: (op: OutboxOp) => Promise<void>;
  available: () => boolean;
  refresh: () => Promise<void>;
  forget: (sessionId: string) => Promise<void>;
}

/**
 * Throw the night away, and leave nothing of it on this device either.
 *
 * Three steps, and the order is the whole function.
 *
 * **The server goes first.** It is the only participant that can see every
 * rating — on a QR night the guests wrote theirs straight over HTTP and this
 * device has never held them — so it owns the cascade. Dropping the local queue
 * first and then failing to reach the server would throw away ratings over a
 * night that still exists.
 *
 * **Then the queued ratings of this night are dropped.** Not pushed: the sync
 * guard now refuses a rating whose session is tombstoned, so anything left in
 * the queue would dead-letter and put "1 refused write" on the sync screen for a
 * night the host deliberately deleted — an error report for something that went
 * exactly as asked. `settle(op, null)` takes the op out of the queue and leaves
 * the local row alone, which is right: the row itself goes in the pull below.
 *
 * **Then a sync.** The tombstones come back down it and `pull` removes the rows,
 * which is what actually clears the shelf, the game page and the draft tile.
 * Awaited rather than fired off, because the screen that called this is about to
 * navigate to a list that reads those rows — a background pull would land after
 * the deleted night had already been rendered one more time.
 *
 * A failed sync is not a failed delete. The night is gone on the server and will
 * be gone here on the next pull, so this reports success and lets the transport's
 * own retry schedule finish the job.
 *
 * **A `404` is not a failed delete either, and treating it as one stranded the
 * night forever.** The server answers 404 for a session it does not hold — one
 * already deleted from another device, or one whose rows this device is holding
 * against a server that no longer has them. The step that clears the card is the
 * pull, and a pull can only carry a tombstone the server actually minted, so
 * there was nothing behind the error message: every retry got the same 404 and
 * the night outlived every reload. The row is gone up there either way, which is
 * the whole of what the person asked for, so this takes it off the device by hand
 * (`forgetSession`) and reports the delete as done. `rows: 0` says honestly that
 * this call tombstoned nothing.
 *
 * Only 404. Any other refusal — 401, 403, a 5xx — means the night may well still
 * be up there, and dropping the local copy would hide a live session from the one
 * device that could delete it.
 *
 * Dependencies are injected so the ordering can be tested without a network.
 */
export async function cancelSession(
  sessionId: string,
  deps: Partial<CancelDeps> = {},
): Promise<DeleteSessionResult> {
  const remove = deps.remove ?? deleteSession;
  const queued = deps.queued ?? (() => pending(QUEUE_SCAN_LIMIT));
  const drop = deps.drop ?? ((op: OutboxOp) => getOutboxStorage().settle(op, null));
  const available = deps.available ?? idbAvailable;
  const refresh = deps.refresh ?? syncNow;
  const forget = deps.forget ?? forgetSession;

  const result = await remove(sessionId).catch((err: unknown) => {
    if (err instanceof ApiError && err.status === 404) return null;
    throw err;
  });

  if (result === null && available()) await forget(sessionId);

  if (available()) {
    for (const op of sessionRatingOps(sessionId, await queued())) {
      await drop(op).catch(() => undefined);
    }
    await refresh().catch(() => undefined);
  }

  return result ?? { session_id: sessionId, rows: 0, grants_revoked: 0 };
}

/** What an arriving device is shown before it picks a seat. */
export interface JoinRoster {
  session_id: string;
  game_title: string;
  game_thumb_url: string | null;
  game_image_url: string | null;
  status: "DRAFT" | "COMPLETED";
  expires_at: number | null;
  participants: { id: string; name: string; colour: string; claimed: boolean }[];
}

export function fetchJoinRoster(code: string): Promise<JoinRoster> {
  return request<JoinRoster>(`/api/join/${encodeURIComponent(code)}`);
}

/**
 * What the claim route actually puts on the wire.
 *
 * Deliberately not `ClaimResult`: the server sends snake_case and withholds
 * `owner_id` entirely, because a guest device has no business knowing whose
 * library it is rating in. Typing this as the internal result would hand every
 * caller three fields that arrive `undefined`.
 */
export interface ClaimWire {
  token: string;
  session_id: string;
  participant_id: string;
}

export function claimSeat(code: string, participantId: string): Promise<ClaimWire> {
  return request<ClaimWire>(`/api/join/${encodeURIComponent(code)}/claim`, {
    method: "POST",
    body: JSON.stringify({ participant_id: participantId }),
  });
}

export interface BggHit {
  bggId: number;
  title: string;
  year: number | null;
  /** Box art from the bundled index. Null when BGG's own search answered. */
  thumbUrl: string | null;
}

export type BggSearchResponse =
  | { ok: true; data: BggHit[]; stale: boolean }
  | { ok: false; reason: string; detail: string };

/**
 * BGG search. Never on the critical path: the caller treats any failure as
 * "type the title in", because the licence terms, the token lead time and a
 * basement wifi all produce the same answer here and none of them may stop a
 * group rating the game they just played.
 */
export async function searchGames(query: string, signal?: AbortSignal): Promise<BggHit[]> {
  const res = await fetch(`/api/bgg/search?q=${encodeURIComponent(query)}`, {
    credentials: "same-origin",
    signal,
  });
  if (!res.ok) throw new ApiError(`search unavailable`, res.status);
  const body = (await res.json()) as BggSearchResponse;
  if (!body.ok) throw new ApiError(body.detail || body.reason, 503);
  return body.data;
}

// ---------------------------------------------------------------------------
// The write seam
// ---------------------------------------------------------------------------

export type Role = "owner" | "guest";
export type WritePath = "outbox" | "http";

/**
 * Where a rating goes.
 *
 * A guest never has an outbox. The owner on a shared device always uses one —
 * that is the offline-first path the whole sync protocol exists for. The owner
 * hosting a QR night writes over HTTP like everyone else at the table, so all
 * the evening's ratings take one road and the reveal gate sees them in one
 * place.
 */
export function writePathFor(mode: CaptureMode, role: Role): WritePath {
  if (role === "guest") return "http";
  return mode === "SHARED_DEVICE" ? "outbox" : "http";
}

/**
 * The offline line, told for the write path actually in use.
 *
 * "Saved on this device and sent when you are back" is only true where the
 * outbox runs. A guest phone, and the owner hosting a QR night, both write over
 * HTTP and genuinely cannot hold a rating locally — telling them otherwise
 * invites the table to keep tapping into a screen that is dropping answers.
 */
export function offlineLine(path: WritePath): string {
  return path === "outbox"
    ? "Offline. Ratings are saved on this device and sent when you are back."
    : "Offline. This device cannot send ratings until you are back.";
}

// ---------------------------------------------------------------------------
// Ratings that did not make it
// ---------------------------------------------------------------------------

/**
 * The keys of ratings this device typed and could not send.
 *
 * The HTTP path has no outbox and is not getting one today: replicating an
 * owner's whole library onto a phone that was borrowed for one evening is not a
 * trade anybody made, and the guest device has no store, no cursor and no
 * account to hang one off. So a failed write lives in the rating screen's retry
 * list and nowhere else, and that list is memory — a reload, a tab iOS reclaims
 * while somebody reads a rulebook, a stray swipe, and the number is gone with
 * nobody told. Losing a rating in silence is the worst thing this screen can do.
 *
 * This is the only warning the path can honestly give: while anything is unsent,
 * the browser is asked to confirm leaving the page. It is not durability and it
 * does not pretend to be — see `offlineLine`, which says out loud that this
 * device cannot hold a rating.
 */
const unsentKeys = new Set<string>();
let unloadArmed = false;

function confirmUnload(event: BeforeUnloadEvent): void {
  event.preventDefault();
}

/** Arms and disarms the unload guard; never throws where there is no window. */
function armUnloadGuard(): void {
  const wanted = unsentKeys.size > 0;
  if (wanted === unloadArmed) return;
  try {
    if (wanted) window.addEventListener("beforeunload", confirmUnload);
    else window.removeEventListener("beforeunload", confirmUnload);
    unloadArmed = wanted;
  } catch {
    // No window, or a browser that refuses the listener: a test harness or the
    // service worker. Nothing to guard and nothing worth failing a write over.
  }
}

/**
 * Everything that has to happen when that list changes.
 *
 * The page guard is the local half. The other half is telling the transport,
 * which owns the one retry schedule in the app: this path has no queue to notice
 * a failure and no event to ride, so unless it says out loud that it is holding
 * something, nothing ever comes back for it and the screen's "Retrying on its
 * own" is a sentence about nothing.
 */
function unsentChanged(): void {
  armUnloadGuard();
  reportUnsentWrites(unsentKeys.size);
}

/** The rating keys currently unsent on this device. Exported to be steppable. */
export function unsentRatings(): string[] {
  return [...unsentKeys];
}

/**
 * A rating the server never took.
 *
 * The message is the sentence shown to whoever typed it, and the screen prints
 * it straight into a line about the table, so a server that explained itself
 * keeps its own words — "this game night is already finished" is not "check the
 * wifi". Everything else gets the app's own line: a transport that never got an
 * answer has nothing to quote, and a 5xx quotes a status at somebody holding a
 * phone at a game table (`…has not reached the table yet. Internal Server Error`
 * is a real thing this said out loud). Both are unsent either way, which is what
 * the guard counts.
 */
export class UnsentRating extends Error {
  readonly key: string;
  constructor(key: string, cause: unknown) {
    super(
      cause instanceof ApiError && cause.serverSaid
        ? cause.message
        : "That rating did not reach the table. This phone cannot hold it, so keep this screen open.",
    );
    this.name = "UnsentRating";
    this.key = key;
    this.cause = cause;
  }
}

/**
 * A rating the server turned down, and will turn down again.
 *
 * The counterpart to `UnsentRating`, and the whole point of telling the two
 * apart: an unsent rating is owed to the table and this device is holding it; a
 * refused one is not owed to anybody. It is not in `unsentKeys`, so nothing
 * retries it, nothing counts it as pending, and the page is not held on its
 * account — there is nothing left on this phone to lose.
 *
 * The message is the server's own sentence wherever there is one, because the
 * two refusals a table actually meets are "that answer is already revealed" and
 * "that seat is on another device", and those call for completely different
 * things from the person holding the phone. Only the server knows which it is.
 */
export class RefusedRating extends Error {
  readonly key: string;
  constructor(key: string, cause: unknown) {
    super(
      cause instanceof ApiError && cause.serverSaid
        ? cause.message
        : "The table would not take that rating, and asking again will not change it.",
    );
    this.name = "RefusedRating";
    this.key = key;
    this.cause = cause;
  }
}

/**
 * A stable local id for one participant's rating of one target.
 *
 * The outbox path needs an id before the server has seen the row, and the same
 * cell must land on the same id every time or a re-rating would insert a second
 * row and trip the `UNIQUE(participant_id, session_aspect_id)` constraint. The
 * shape is deterministic rather than a ULID for exactly that reason.
 */
export function ratingRowId(targetId: string, participantId: string): string {
  return `${targetId}~${participantId}`;
}

/**
 * Whether moving on from a step has to write an n/a row first.
 *
 * The reveal gate counts committed rows, so leaving a step without one keeps the
 * aspect blind for the whole table — and there is no way back to it. Both ways
 * of moving on ask this: the shared device's "Skip — pass it on" and, on a QR
 * night, the plain "Next". Only the shared-device branch used to, which left one
 * player's untouched aspect blocking everybody else's evening.
 *
 * Never for the headline (the one number nobody skips), never for a step this
 * person has already answered, and never without a seat to write it against.
 */
export function skipRowNeeded(
  stepKind: "aspect" | "subquestion" | "headline",
  actorId: string | null,
  answered: boolean,
): boolean {
  return stepKind !== "headline" && actorId !== null && !answered;
}

export type RatingTarget =
  | { kind: "headline"; value: number }
  | { kind: "overall"; value: number }
  | {
      kind: "aspect";
      sessionAspectId: string;
      value: number | null;
      na: boolean;
      note?: string | null;
    }
  | {
      kind: "subquestion";
      sessionSubQuestionId: string;
      value: number | null;
      na: boolean;
      note?: string | null;
    };

/**
 * The row one aspect answer becomes.
 *
 * The table only accepts `(na = 1 AND value IS NULL)` or `(na = 0 AND value IS
 * NOT NULL)`, so every answer without a number — a skip, or a sentence typed
 * instead of a number — is stored as n/a. That is not cosmetic: the reveal gate
 * counts committed rows, so an answer that writes nothing leaves the aspect
 * blind forever and the table waiting on somebody who has already had their
 * turn. The three cases are told apart on screen by the note, not by `na`.
 */
export function aspectTarget(
  sessionAspectId: string,
  answer: { value: number | null; na: boolean; note?: string | null },
): RatingTarget {
  const note = (answer.note ?? "").trim();
  const na = answer.na || answer.value === null;
  return {
    kind: "aspect",
    sessionAspectId,
    value: na ? null : answer.value,
    na,
    note: note === "" ? null : note,
  };
}

/**
 * The same row for a follow-up.
 *
 * Identical rule, different table: `subquestion_rating` carries the same
 * `(na = 1 AND value IS NULL)` check, and its reveal gate counts committed rows
 * the same way, so an answer without a number is n/a here too.
 */
export function subQuestionTarget(
  sessionSubQuestionId: string,
  answer: { value: number | null; na: boolean; note?: string | null },
): RatingTarget {
  const note = (answer.note ?? "").trim();
  const na = answer.na || answer.value === null;
  return {
    kind: "subquestion",
    sessionSubQuestionId,
    value: na ? null : answer.value,
    na,
    note: note === "" ? null : note,
  };
}

/**
 * The row id a rating lands on: the aspect, the sub-question, or — for the two
 * that have no target row of their own — the session.
 *
 * The overall gets `<session>:overall` rather than the bare session id, and the
 * suffix is load-bearing on the client even though the two rows are in different
 * tables and could not collide in storage. `unsentKeys` is one flat set across
 * every table, keyed by this id: share it and an overall that finally lands
 * clears the "not sent" mark from the headline sitting beside it, on the one
 * screen whose whole job is to say honestly what has reached the table.
 *
 * Written once, in `overallRowId`, because `sessionRatingOps` matches the outbox
 * on the same shape 480 lines up: change the suffix in one place only and that
 * guard silently matches nothing, which dead-letters a queued rating at the
 * moment the host completes the night, with no error anywhere.
 */
function overallRowId(sessionId: string): string {
  return `${sessionId}:overall`;
}

function rowTargetOf(target: RatingTarget, sessionId: string): string {
  if (target.kind === "headline") return sessionId;
  if (target.kind === "overall") return overallRowId(sessionId);
  return target.kind === "aspect" ? target.sessionAspectId : target.sessionSubQuestionId;
}

function entityOf(target: RatingTarget): Entity {
  if (target.kind === "headline") return "headline_rating";
  if (target.kind === "overall") return "overall_rating";
  return target.kind === "aspect" ? "aspect_rating" : "subquestion_rating";
}

export interface CommitArgs {
  sessionId: string;
  participantId: string;
  mode: CaptureMode;
  role: Role;
  token?: string | null;
  target: RatingTarget;
}

/**
 * Write one rating.
 *
 * The HTTP path returns the server's own answer including whether that write
 * closed the reveal gate. The outbox path returns `revealed: false` always —
 * not because nothing revealed, but because this device does not know and must
 * not guess. The reveal arrives from the server, over the socket or on the next
 * read, and the screen says so honestly until it does.
 */
export async function commitRating(args: CommitArgs): Promise<{ revealed: boolean }> {
  const path = writePathFor(args.mode, args.role);

  if (path === "http") {
    const key = ratingRowId(rowTargetOf(args.target, args.sessionId), args.participantId);
    const common = { session_id: args.sessionId, participant_id: args.participantId };
    const body =
      args.target.kind === "headline" || args.target.kind === "overall"
        ? { target: args.target.kind, ...common, value: args.target.value }
        : {
            target: args.target.kind,
            ...common,
            ...(args.target.kind === "aspect"
              ? { session_aspect_id: args.target.sessionAspectId }
              : { session_subquestion_id: args.target.sessionSubQuestionId }),
            value: args.target.na ? null : args.target.value,
            na: args.target.na,
            note: args.target.note ?? null,
          };
    let result: SubmitRatingResult;
    try {
      result = await request<SubmitRatingResult>("/api/ratings", {
        method: "POST",
        body: JSON.stringify(body),
        token: args.token,
      });
    } catch (err) {
      // A refusal is the end of this write, not a step on the ladder. Removed
      // rather than merely not added: the shape QA watched is a write that
      // genuinely failed first, was kept and retried, and met the reveal on the
      // way back — so it has to leave the list on the way out, or the screen
      // keeps promising the table a rating that is never coming.
      if (permanentRefusal(err)) {
        unsentKeys.delete(key);
        unsentChanged();
        throw new RefusedRating(key, err);
      }
      // Nothing was written anywhere. Say so, and hold the page open until it
      // is retried — the alternative is a reload that takes the number with it.
      unsentKeys.add(key);
      unsentChanged();
      throw new UnsentRating(key, err);
    }
    unsentKeys.delete(key);
    unsentChanged();
    return { revealed: result.revealed };
  }

  const entity = entityOf(args.target);
  const id = ratingRowId(rowTargetOf(args.target, args.sessionId), args.participantId);
  const existing = await readLocal(entity, id);
  const baseVersion = typeof existing?.version === "number" ? existing.version : 0;

  const patch =
    args.target.kind === "headline" || args.target.kind === "overall"
      ? {
          session_id: args.sessionId,
          participant_id: args.participantId,
          value: args.target.value,
        }
      : {
          session_id: args.sessionId,
          participant_id: args.participantId,
          ...(args.target.kind === "aspect"
            ? { session_aspect_id: args.target.sessionAspectId }
            : { session_subquestion_id: args.target.sessionSubQuestionId }),
          value: args.target.na ? null : args.target.value,
          na: args.target.na ? 1 : 0,
          note: args.target.note ?? null,
        };

  await mutate(entity, id, patch, baseVersion);
  // Push immediately: the reveal is a server decision, so an unsynced rating is
  // a rating the table is still waiting on. Failure here is not an error — the
  // op is durable and sync retries on its own schedule.
  void syncNow().catch(() => undefined);
  return { revealed: false };
}

/** Creates the guest profile / participant a "someone else joined" flow needs. */
export function newParticipantId(): string {
  return ulid();
}

export type { SessionParticipant };
